import { notification } from 'antd'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useRecoilState, useResetRecoilState } from 'recoil'
import { getCard, getProfile } from '../repositories/apiRepo'
import {
  logout as callLogoutRepo,
  clearAxiosAuthorization,
  clearLocalStorage,
  getToken,
  setAxiosAuthorization
} from '../repositories/authRepo'
import { cardAtom } from '../store/card'
import { isLoadingProfileAtom } from '../store/isLoadingProfile'
import { userProfileAtom } from '../store/userProfile'
import { logoutEmitter } from './eventEmitter'
import UserProfile from '../models/userProfile'
import { toCamelCase } from '../utils'

let isFetchingProfile = false

// get token from local storage then fetch user profile
const useAuth = () => {
  const navigate = useNavigate()
  const resetCurrentUser = useResetRecoilState(userProfileAtom)
  const resetCard = useResetRecoilState(cardAtom)
  const [userProfile, setUserProfile] = useRecoilState(userProfileAtom)
  const [isLoadingProfile, setIsLoadingProfile] =
    useRecoilState(isLoadingProfileAtom)
  const [, setCard] = useRecoilState(cardAtom)

  const [isRefetch, setIsRefetch] = useState<boolean>(false)

  const refetch = () => setIsRefetch((prev) => !prev)

  const logout = (shouldCallLogoutApi = true) => {
    if (shouldCallLogoutApi) {
      callLogoutRepo()
    }

    clearLocalStorage()
    clearAxiosAuthorization()
    // reset recoil atom
    resetCurrentUser()
    resetCard()
    navigate('/login')
  }

  useEffect(() => {
    const getUserProfile = async () => {
      try {
        const profile = await getProfile()
        const card = await getCard()

        if (card.data) {
          setCard({
            cardNumber: card.data.card_number,
            cardExpireAt: card.data.card_expire_at,
            cardHolderName: card.data.card_holder_name
          })
        }
        setUserProfile(toCamelCase<UserProfile>(profile) as UserProfile)
      } catch (error) {
        const message = (error as any)?.response?.data?.error?.message

        if (message) {
          notification.error({
            message: 'エラー発生しました',
            description: message
          })
        }
      }
    }
    // do nothing if user are login
    if (userProfile || isFetchingProfile) {
      return
    }

    isFetchingProfile = true

    // get token from localStorage
    const token = getToken()
    if (!token || token.expire_time < Date.now() - 1000) {
      logout(false)
      return
    }

    setAxiosAuthorization(token.access_token, token.token_type)
    setIsLoadingProfile(true)

    getUserProfile().finally(() => {
      isFetchingProfile = false
      setIsLoadingProfile(false)
    })
  }, [userProfile, isRefetch])

  useEffect(() => {
    logoutEmitter.on('eventLogout', () => {
      logout(false)
    })

    return () => {
      logoutEmitter.removeAllListeners('eventLogout')
    }
  }, [])

  return { logout, userProfile, isLoadingProfile, refetch }
}

export default useAuth
